import { getFirestore, setDoc } from "@firebase/firestore";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import { useTranslations } from "next-intl";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import ReactMarkdown from "react-markdown";
import { ISourceOptions } from "react-tsparticles";
import { useMeasure } from "react-use";
import { useForm, usePlugin } from "tinacms";
import Banner from "../common/components/Banner";
import Header from "../common/components/Header";
import { ABOUT_US_CONFIG, Configs } from "../common/lib/api/configs";
import { useBreakpoint } from "../common/lib/hooks/useWindow";
import { makeAdmin } from "../common/lib/makeAdmin";
import { makeFirebase } from "../common/lib/makeFirebase";
import particleConfig from "../common/particlesjs-config.json";
import {
  HEADER_HEIGHT_MEDIUM,
  HEADER_HEIGHT_MOBILE,
} from "../common/styles/mixins";
import { makeAboutUsForm } from "../forms/aboutUsForm";
import { Container, Content, SkewedDiv, WhiteSkewedDiv } from "../styles/About";
import { Glass } from "../styles/Home";

const Particles = dynamic(() => import("react-tsparticles"), { ssr: false });

export const getStaticProps = async ({
  locale: baseLocale,
}: GetStaticPropsContext) => {
  const store = (await makeAdmin()).firestore();
  const locale = baseLocale || "en";
  const aboutUs: ABOUT_US_CONFIG | null = await store
    .collection("configs")
    .doc(`${locale}.about_us`)
    .get()
    .then((snapshot) => {
      if (!snapshot.exists) return null;
      return snapshot.data() as ABOUT_US_CONFIG;
    });
  return { props: { aboutUs }, revalidate: 60 };
};

const AboutPage: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  aboutUs,
}) => {
  const t = useTranslations("About");
  const [ref, dim] = useMeasure<any>();
  const isMedium = useBreakpoint("MEDIUM");
  const { locale } = useRouter();
  const [data, form] = useForm<ABOUT_US_CONFIG>(
    makeAboutUsForm(aboutUs === null ? undefined : aboutUs, {
      onSubmit: (val) => {
        const store = getFirestore(makeFirebase());
        const about = new Configs(store).aboutUsRef(locale || "en");
        return setDoc(about, val);
      },
      // onSubmit: (formData) => {
      //   const cfgs = new Configs(makeFirebase().firestore());
      //   return cfgs
      //     .mainPageRef(locale || "en")
      //     .set(formData, { merge: true });
      // },
    })
  );
  usePlugin(form);
  return (
    <>
      <NextSeo title={t("about") as string} />
      <Header className="" />
      <Banner header ref={ref}>
        <SkewedDiv style={{ position: "absolute", inset: 0 }} noContent>
          <div style={{ height: "100%", width: "100vw" }}>
            <Particles
              height={`${
                dim.height +
                2 * (isMedium ? HEADER_HEIGHT_MEDIUM : HEADER_HEIGHT_MOBILE)
              }px`}
              width={"100vw"}
              options={particleConfig as unknown as ISourceOptions}
              style={{ padding: 0, width: "100%" }}
            />
          </div>
        </SkewedDiv>
        <Content>
          <h1>{data.title || t("about")}</h1>
          <p>
            {data.subtitle ||
              "Here are the answers to all your burning questions"}
          </p>
        </Content>
      </Banner>
      <Container>
        <WhiteSkewedDiv>
          <div>
            <div>
              <h3 className="intro">{data.sections?.[0]?.title}</h3>
              <ReactMarkdown components={{ h1: "h2", h2: "h3", h3: "h4" }}>
                {data.sections?.[0]?.content}
              </ReactMarkdown>
            </div>
            <div className="illustration_container">
              <div>
                <img src="/vr_illu_black.png" />
              </div>
              <div>
                <h3>{data.sections?.[1]?.title}</h3>
                <ReactMarkdown components={{ h1: "h2", h2: "h3", h3: "h4" }}>
                  {data.sections?.[1]?.content}
                </ReactMarkdown>
              </div>
            </div>
            <div className="illustration_container">
              <div>
                <h3>{data.sections?.[2]?.title}</h3>
                <ReactMarkdown components={{ h1: "h2", h2: "h3", h3: "h4" }}>
                  {data.sections?.[2]?.content}
                </ReactMarkdown>
              </div>
            </div>
            <div className="illustration_container reversed">
              <div>
                <img src="/blockchain_illu_black.png" />
              </div>
              <div>
                <h3>{data.sections?.[3]?.title}</h3>
                <ReactMarkdown components={{ h1: "h2", h2: "h3", h3: "h4" }}>
                  {data.sections?.[3]?.content}
                </ReactMarkdown>
              </div>
            </div>
            <div className="illustration_container reversed">
              <div>
                <h3>{data.sections?.[4]?.title}</h3>
                <ReactMarkdown components={{ h1: "h2", h2: "h3", h3: "h4" }}>
                  {data.sections?.[4]?.content}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        </WhiteSkewedDiv>
        <SkewedDiv noContent>
          <Banner>
            <Particles
              id="tsparticles-2"
              height={`100%`}
              options={{ ...particleConfig } as unknown as ISourceOptions}
            />
            <Content noPadding>
              <div className="horizontal_container">
                <div>
                  <h2>{t("mission")}</h2>
                  <p>{data?.principles?.[0]}</p>
                </div>
                <div>
                  <h2>{t("vision")}</h2>
                  <p>{data?.principles?.[1]}</p>
                </div>
                <div>
                  <h2>{t("values")}</h2>
                  <p>{data?.principles?.[2]}</p>
                </div>
              </div>
            </Content>
          </Banner>
        </SkewedDiv>
      </Container>
    </>
  );
};

export default AboutPage;
